import { DownOutlined } from '@ant-design/icons';
import { FishingReport, WaterbodyDetail } from '@omniafishing/core';
import { Collapse } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router';
import OmniaCarousel from '../../components/carousel/omnia_carousel';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import { CurrentConditionsTimestamp } from '../../components/current_conditions/current_conditions_timestamp';
import { CurrentConditionsMap } from '../../components/current_conditions_map/current_conditions_map';
import { MapHotbaitCard } from '../../components/map/map_modals/lakes_modal/map_hotbait_card';
import { MapQueryParams } from '../../components/map_default/map_default';
import { OmniaButton } from '../../components/omnia_button/omnia_button';
import { ProTrialButton } from '../../components/pro_buttons/pro_trial_button';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { WaterbodyDetailHeader } from '../../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_header/waterbody_detail_header';
import { useListAttributionUuid } from '../../hooks/use_list_attribution_uuid';
import { useQueryString } from '../../hooks/use_query_string';
import { useResponsive } from '../../hooks/use_responsive';
import { apiV1 } from '../../lib/api';
import { fitBounds } from '../../lib/fit_bounds';
import { PRO_TRIAL_PURCHASE_URL } from '../../lib/stripe';
import { WebAnalytics } from '../../lib/web_analytics';
import { RoutePaths } from '../../routes';
import { ExtendedMapQueryParams } from '../map_page/lib/map_page_types';
import styles from './waterbody_current_conditions_page.less';

dayjs.extend(utc);
interface WaterbodyCurrentConditionsPageProps {
  waterbodyDetail: WaterbodyDetail;
}

export enum ConditionsPanelKeys {
  CurrentWaterTemperature = '1',
  UpdateFrequency = '2',
}

export const WaterbodyCurrentConditionsPage = (props: WaterbodyCurrentConditionsPageProps) => {
  const { waterbodyDetail } = props;
  const { isDesktop, isMobile } = useResponsive();
  const history = useHistory();
  const { stringify } = useQueryString();

  const {
    lat,
    lng,
    water_temperature,
    primary_name,
    water_temperature_recorded_at,
    url_slug,
    season_groups = [],
    bbox,
  } = waterbodyDetail || {};
  const activeSeasonGroup = season_groups.find((season_group) => season_group.active);

  const [fishingReports, setFishingReports] = React.useState<FishingReport[]>([]);
  const listUuid = useListAttributionUuid();
  const nowRef = useRef(new Date().toISOString());

  useEffect(() => {
    if (!waterbodyDetail) {
      return;
    }

    apiV1
      .waterbodyFishingReportsFetch(url_slug, {
        sort: 'most_relevant',
        per_page: 8,
        season_group: activeSeasonGroup?.name,
      })
      .then((res) => {
        setFishingReports(res.data.data.filter((fr) => fr.featured_product != null));
      });
  }, [waterbodyDetail]);

  if (!waterbodyDetail) {
    return null;
  }

  const lastUpdatedFormattedDate = dayjs(water_temperature_recorded_at).format('MMMM D, YYYY');

  const { Panel } = Collapse;

  const Faqs = (
    <section aria-labelledby="faq-heading" className={styles.section__faq}>
      <h2 className={styles.h2}>
        Frequently Asked Questions About {primary_name} Water Temperature
      </h2>
      <div>
        <Collapse
          expandIconPosition="end"
          expandIcon={({ isActive }) => <DownOutlined rotate={isActive ? 180 : 0} />}
        >
          <Panel
            header={<h3>What is the current water temperature of {primary_name}?</h3>}
            key={ConditionsPanelKeys.CurrentWaterTemperature}
          >
            <div className={styles.faqPanel}>
              <p>
                The latest recorded temperature is <strong>{water_temperature}º Fahrenheit</strong>
              </p>
            </div>
          </Panel>

          <Panel
            header={<h3>How often is the {primary_name} water temperature updated?</h3>}
            key={ConditionsPanelKeys.UpdateFrequency}
          >
            <div className={styles.faqPanel}>
              <p>
                Water temperature data for {primary_name} is updated regularly. The last update was
                made on {lastUpdatedFormattedDate}. We ensure that the most recent and accurate
                temperature readings are available to you, with updates occurring throughout the
                week.
              </p>
            </div>
          </Panel>
        </Collapse>
      </div>
    </section>
  );

  const CurrentConditionsMapElement = <CurrentConditionsMap waterbodyDetail={waterbodyDetail} />;
  return (
    <ScrollToTop>
      <section>
        <WaterbodyDetailHeader
          headingTag="p"
          productToShowInFollowModal={null}
          scrollToFishingReport={() => null}
          waterbody={waterbodyDetail}
          lastUpdatedDate={water_temperature_recorded_at}
          showPublishDates
          navElement={null}
          showBreadcrumbs={false}
          showButtons={false}
        />
        <section className={styles.content}>
          <ContentWrapper>
            <div className={styles.top}>
              <div className={styles.col}>
                {isMobile && (
                  <>
                    {CurrentConditionsMapElement}{' '}
                    <ProTrialButton
                      size="lg"
                      kind="secondary"
                      text="Try PRO 7-Days FREE"
                      block
                      trialPaymentLinkUrl={PRO_TRIAL_PURCHASE_URL}
                      className="mb-4"
                      onClick={() => {
                        WebAnalytics.proButtonClicked({
                          location: 'current_conditions_hero',
                          type: 'trial',
                          style: 'button',
                        });
                      }}
                    />
                  </>
                )}
                <div className={styles.titleBlurb}>
                  <h1 className={styles.h1}>{primary_name} Water Temperature</h1>
                  <p>
                    Access the latest and most accurate water temperature data for {primary_name}.
                    Currently the water temperature is{' '}
                    <strong>{water_temperature}º Fahrenheit</strong>. This page provides real-time
                    updates and detailed insights into the water conditions of {primary_name} to
                    help anglers succeed.
                  </p>
                </div>

                <div className="border-b border-[#061622] pb-6 mb-6">
                  <CurrentConditionsTimestamp
                    lat={lat}
                    lng={lng}
                    timestamp={nowRef.current}
                    isProBlocked={false}
                    className={styles.conditions}
                  />
                </div>

                <section>
                  <h2 className="text-base font-bold mb-3">
                    Here's what local anglers are catching in {activeSeasonGroup?.display_name}
                  </h2>
                  <div className={'-mx-3 mb-3'}>
                    <OmniaCarousel
                      key={fishingReports.map((fr) => fr.id).join('')}
                      adaptive
                      arrows
                      newArrows
                    >
                      {fishingReports.map((report, i) => (
                        <div key={report.id} className={'w-full px-3 sm:w-[49.99%]'}>
                          <MapHotbaitCard
                            fishingReport={report}
                            listUuid={listUuid}
                            position={i}
                            className="!bg-[#F1F4F3] !border-none"
                          />
                        </div>
                      ))}
                    </OmniaCarousel>
                  </div>

                  <OmniaButton
                    kind="primary"
                    size="md"
                    className="flex ml-auto mr-auto"
                    onClick={() => {
                      let newZoom = 12;
                      const width = window.innerWidth;
                      const height = window.innerHeight;

                      if (bbox && width && height) {
                        newZoom = fitBounds(
                          [
                            [bbox[0][0], bbox[1][1]],
                            [bbox[2][0], bbox[3][1]],
                          ],
                          {
                            width,
                            height,
                          }
                        ).zoom;
                      }

                      const params: Partial<ExtendedMapQueryParams> = {
                        lat,
                        lng,
                        zoom: newZoom,
                        [MapQueryParams.waterbody_slug]: url_slug,
                        tab: 'fishing_reports',
                      };

                      history.push({
                        pathname: RoutePaths.MAP,
                        search: stringify(params),
                      });
                    }}
                  >
                    View More Local Reports
                  </OmniaButton>
                </section>
              </div>

              <div className={styles.sidebar}>
                {isDesktop && (
                  <>
                    {CurrentConditionsMapElement}{' '}
                    <ProTrialButton
                      size="lg"
                      kind="secondary"
                      text="Try PRO 7-Days FREE"
                      trialPaymentLinkUrl={PRO_TRIAL_PURCHASE_URL}
                      onClick={() => {
                        WebAnalytics.proButtonClicked({
                          location: 'current_conditions_hero',
                          type: 'trial',
                          style: 'button',
                        });
                      }}
                    />
                    {Faqs}
                  </>
                )}
              </div>
            </div>

            {isMobile && Faqs}
          </ContentWrapper>
        </section>
      </section>
    </ScrollToTop>
  );
};
